import { type FunctionComponent, useState } from 'react'
import React from 'react'
import styled from '@emotion/styled'
import {
  typography,
  ItemsList,
  Divider,
  Flex,
  SelectField,
  Text,
  spacing,
} from '@retailer-platform/shared-components/src/tds'
import { usePartnerContext } from '@retailer-platform/dashboard/utils'
import { trackEvent } from '@retailer-platform/dashboard/analytics'
import { type NotificationsNotificationChannel } from '../../../../api'
import { useDomainMessages } from '../../../../utils/domain/intl'
import { COLUMN_WIDTHS } from '../types'
import { useBannerNotificationChannels } from '../hooks/useNotificationChannelsQuery'
import { useIsInAppEnabled } from '../hooks/useIsInAppEnabled.hooks'
import { useUnsubscribeParams } from '../hooks/useUnsubscribeParams'
import { NotificationSettingRow } from './NotificationSettingRow'

const ColumnHeader = styled.div({
  ...typography.bodyEmphasized,
})

const BannerSelectorWrapper = styled.div({
  width: '100%',
  maxWidth: '300px',
  marginTop: '16px',
})

const SectionDescription = styled.div(({ theme }) => ({
  marginTop: '8px',
  marginBottom: '8px',
  color: theme.colors.systemGrayscale80,
}))

const BannerHeaderWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginTop: '32px',
  marginBottom: '16px',
})

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.colors.systemDetrimentalRegular,
  marginBottom: '16px',
}))

const InfoMessage = styled.div(({ theme }) => ({
  color: theme.colors.systemGrayscale80,
  marginBottom: '16px',
}))

export const BannerChannelsSection: FunctionComponent = () => {
  const partnerContext = usePartnerContext()
  const unsubscribeParams = useUnsubscribeParams()
  const [selectedBanner, setSelectedBanner] = useState<string>('')
  const [bannerChannels, setBannerChannels] = useState<NotificationsNotificationChannel[]>([])
  const [bannerError, setBannerError] = useState<string | null>(null)
  // const [isLoadingBanner, setIsLoadingBanner] = useState(false)

  const i18n = useDomainMessages({
    bannerTitle: 'notificationsDomain.list.bannerSpecificSettings.pageTitle',
    bannerDesc: 'notificationsDomain.list.bannerSpecificSettings.pageDescription',
    eventName: 'notificationsDomain.list.columnTitle.eventName',
    description: 'notificationsDomain.list.columnTitle.description',
    partner: 'notificationsDomain.list.columnTitle.partner',
    email: 'notificationsDomain.list.columnTitle.email',
    inApp: 'notificationsDomain.list.columnTitle.inApp',
    banner: 'notificationsDomain.list.columnTitle.banner',
    selectBanner: 'notificationsDomain.list.placeholder.selectBanner',
    loadError: 'notificationsDomain.list.notification.loadError',
    unknownBanner: 'notificationsDomain.list.unknownBanner',
    noNotificationsForBanner: 'notificationsDomain.list.noNotificationsForBanner',
  })

  const isInAppEnabled = useIsInAppEnabled()

  const handleBannerSelect = async (bannerId: string) => {
    if (bannerId === selectedBanner) {
      return
    }
    trackEvent({
      id: 'notifications.settings.banner.selected',
      description: 'User selected a banner',
      data: {
        bannerId,
      },
    })
    setSelectedBanner(bannerId)
    setBannerChannels([])
    setBannerError(null)
  }

  const { data: result, loading, error } = useBannerNotificationChannels(selectedBanner)

  React.useEffect(() => {
    if (error) {
      setBannerChannels([])
      const errorMessage =
        error.graphQLErrors?.[0]?.message ||
        error.networkError?.message ||
        error.message ||
        i18n.loadError

      setBannerError(errorMessage)
    }

    if (result) {
      const channels = result.notificationChannelsForCurrentAccount || []
      setBannerChannels(channels)
      setBannerError(null) // Clear any previous errors
    }
  }, [error, result, i18n.loadError, selectedBanner])

  React.useEffect(() => {
    if (unsubscribeParams?.bannerId) {
      handleBannerSelect(unsubscribeParams.bannerId as string)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedBannerName = selectedBanner
    ? (partnerContext?.warehouses || []).find(w => String(w.id) === selectedBanner)?.name ||
      i18n.unknownBanner
    : undefined

  return (
    <>
      <BannerHeaderWrapper>
        <Text typography="title">{i18n.bannerTitle}</Text>
        <SectionDescription>
          <Text typography="bodyRegular">{i18n.bannerDesc}</Text>
        </SectionDescription>
        <BannerSelectorWrapper>
          <SelectField
            options={(partnerContext?.warehouses || []).map(w => ({
              value: String(w.id),
              label: w.name,
            }))}
            value={selectedBanner}
            onChange={value => handleBannerSelect(value || '')}
            placeholder={i18n.selectBanner}
            compact
            clearable={false}
            allowDeselect={false}
          />
        </BannerSelectorWrapper>
      </BannerHeaderWrapper>
      {bannerError && <ErrorMessage>{bannerError}</ErrorMessage>}
      {selectedBanner && bannerChannels.length === 0 && !bannerError && !loading && (
        <InfoMessage>
          {i18n.noNotificationsForBanner.replace('{bannerName}', selectedBannerName || '')}
        </InfoMessage>
      )}
      {selectedBanner && bannerChannels.length > 0 && (
        <>
          <ItemsList.Section>
            <Flex.Row gap={spacing.s8} alignItems="center">
              <Flex.Column css={{ width: COLUMN_WIDTHS.EVENT_NAME }}>
                <ColumnHeader>{i18n.eventName}</ColumnHeader>
              </Flex.Column>
              <Flex.Column css={{ width: COLUMN_WIDTHS.DESCRIPTION }}>
                <ColumnHeader>{i18n.description}</ColumnHeader>
              </Flex.Column>
              <Flex.Column css={{ width: COLUMN_WIDTHS.PARTNER }}>
                <ColumnHeader>{i18n.partner}</ColumnHeader>
              </Flex.Column>
              <Flex.Column css={{ width: COLUMN_WIDTHS.EMAIL }}>
                <ColumnHeader>{i18n.email}</ColumnHeader>
              </Flex.Column>
              {isInAppEnabled && (
                <Flex.Column css={{ width: COLUMN_WIDTHS.IN_APP }}>
                  <ColumnHeader>{i18n.inApp}</ColumnHeader>
                </Flex.Column>
              )}
              <Flex.Column css={{ width: COLUMN_WIDTHS.BANNER }}>
                <ColumnHeader>{i18n.banner}</ColumnHeader>
              </Flex.Column>
            </Flex.Row>
          </ItemsList.Section>
          <Divider />
          {bannerChannels.map((notification, index) => (
            <React.Fragment key={`banner-${notification.id}-${selectedBanner}`}>
              <NotificationSettingRow
                channel={notification}
                partnerName={partnerContext?.partnerName || 'Unknown'}
                selectedBanner={selectedBanner}
                bannerName={selectedBannerName}
                isDisabled={loading}
                showInApp={isInAppEnabled}
                scopeId={selectedBanner}
              />
              {index < bannerChannels.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  )
}
